import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    QueryList,
    Renderer2,
    TemplateRef,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { MenuItem } from 'primeng/api';
import { DomHandler } from 'primeng/dom';

import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ActionKeys } from '../../enums/keys.enum';
import { LayoutTypesEnum } from '../../../Layout/enums/layout-types.enum';

export interface CommandParams {
    item: any;
    originalEvent: Event;
    data?: any;
    position?: string;
}

export enum PanelPositionEnum {
    top = 'top',
    bottom = 'bottom',
    right = 'right',
    left = 'left'
}

/**
 * ### Accessibility Usage
 * + ***ariaLabel*** - use this to provide an accessible label for the toggle button
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-kebab-menu',
    host: { '[attr.data-id]': 'id' },
    templateUrl: './kebab-menu.component.html',
    styleUrls: ['./kebab-menu.component.scss'],
    animations: [
        trigger('panelState', [
            state('hidden', style({
                opacity: 0,
                transform: 'scaleY(0)'
            })),
            state('visible', style({
                opacity: 1,
                transform: 'scaleY(1)'
            })),
            transition('visible => hidden', animate('.1s linear')),
            transition('hidden => visible', animate('.15s cubic-bezier(0, 0, 0.2, 1)'))
        ])
    ],
    standalone: false
})
export class KebabMenuComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
    @Input() id: string;
    @Input() style?: ButtonTypesEnum = ButtonTypesEnum.icon;
    @Input() label?: string;
    @Input() labelTemplate: TemplateRef<any> = null;
    @Input() tooltip?: string;
    @Input() tooltipPosition?: string = 'right';
    @Input() ariaLabel: string;
    @Input() icon?: string = 'fa-light fa-ellipsis-vertical';
    @Input() disabled?: boolean = false;
    @Input() loading: boolean = false;
    @Input() panelPositionX?: PanelPositionEnum = PanelPositionEnum.left;
    @Input() caretDown: boolean = true;
    @Input() ignoreMouseMove: boolean = false;
    @Input() get options(): MenuItem[] {
        return this._options;
    }
    @Input() data?: any;
    set options(options: MenuItem[]) {
        this._options = options;
        this.optionsToDisplay = this._options;
        this.optionsChanged = true;
    }
    @Input() initiallyFocusable: boolean = true;
    @Input() panelStyle: string;
    @Input() panelClass?: string;
    @Input() isMenuItem: boolean = false;
    @Input() alignmentPriority: LayoutTypesEnum = LayoutTypesEnum.vertical;
    @Input() hidePanelOnMouseLeave: boolean = true;
    @Input() showIcon: boolean = false;
    @Input() parentButton?: HTMLElement;
    @Input() panelAlignCenter: boolean = false;
    @Input() addAtPosition?: string;
    @Input() textAlignment: string;
    @Input() scrollableParentElements?: any[];
    @Input() useScrollPanel: boolean = false;

    @Output() panelShown: EventEmitter<void> = new EventEmitter<void>();
    @Output() panelHidden: EventEmitter<void> = new EventEmitter<void>();
    @Output() itemSelected: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('kebabMenu') kebabMenuRef: ElementRef;
    @ViewChild('panel') panelViewChild: ElementRef;
    @ViewChild('button') buttonViewChild: ElementRef;
    @ViewChildren(KebabMenuComponent) kebabMenus: QueryList<KebabMenuComponent>;

    readonly buttonTypes = ButtonTypesEnum;
    readonly buttonHeight: number = 30;
    readonly positionEnum = PanelPositionEnum;

    public kebabMenuVisible: boolean = false;
    public panelVisible: boolean = false;
    public optionsToDisplay: any[];

    private kebabMenu: HTMLDivElement;
    private panel: HTMLDivElement;
    private button: HTMLDivElement;
    private shown: boolean;
    private documentClickListener: any;
    private optionsChanged: boolean;
    private scrollableParents: any;
    private mouseMoveListener: any;
    private panelPositionY: PanelPositionEnum;

    private _options: MenuItem[];

    private panelPositionYChanged: boolean = false;
    private static currentlyOpen: KebabMenuComponent | null = null;

    needsScroll: boolean = false;

    constructor(
        public el: ElementRef,
        public renderer: Renderer2,
        private cd: ChangeDetectorRef,
        public zone: NgZone
    ) { }

    private readonly scrollListener = () => this.hide();
    private onFocusOutFromKebabMenu = () => {
        this.kebabMenuVisible = false;
        this.kebabMenu.removeEventListener('focusout', this.onFocusOutFromKebabMenu);
    };

    ngAfterViewInit() {
        this.kebabMenu = <HTMLDivElement>this.kebabMenuRef.nativeElement;
        this.panel = <HTMLDivElement>this.panelViewChild.nativeElement;
        this.button = <HTMLDivElement>this.buttonViewChild.nativeElement;
    }

    ngOnChanges(): void {
        this.needsScroll = this.calculateNeedsScroll();
    }


    onItemClick(event, item: MenuItem, hidePanel: boolean = true) {
        event.stopImmediatePropagation();
        this.selectItem(event, item, hidePanel);
    }

    selectItem(event, item: MenuItem, hidePanel: boolean = true) {
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        if (!item.url) {
            event.preventDefault();
        }

        if (item.command) {
            const commandParams: CommandParams = {
                originalEvent: event,
                item: item
            };
            if (this.data) {
                commandParams.data = this.data;
            }
            if (this.addAtPosition) {
                commandParams.position = this.addAtPosition;
            }
            item.command(commandParams);
        }
        if (hidePanel && !item.isLocked) {
            this.hide();
            this.itemSelected.emit();
        }
    }

    ngAfterViewChecked() {
        if (this.shown) {
            this.onShow();
            this.shown = false;
        }

        if (this.optionsChanged && this.panelVisible) {
            this.optionsChanged = false;

            this.zone.runOutsideAngular(() => {
                setTimeout(() => {
                    if (this.panelVisible) {
                        this.alignPanel();
                    }
                }, 1);
            });
        }
    }

    onMouseClick(event) {
        if (this.disabled) {
            return;
        }
        event.stopImmediatePropagation();
        if (this.panelVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    onShow() {
        // Delay registering the document click listener so the click that opened
        // the panel finishes propagating before we start listening for outside clicks.
        setTimeout(() => this.bindDocumentClickListener());
        this.addEventListeners();

        document.body.appendChild(this.panel);

        if (this.options && this.options.length) {
            this.alignPanel();
        }
    }

    show() {
        if (!this.isMenuItem) {
            if (KebabMenuComponent.currentlyOpen && KebabMenuComponent.currentlyOpen !== this) {
                KebabMenuComponent.currentlyOpen.hide(false, true);
            }
            KebabMenuComponent.currentlyOpen = this;
        }
        document.body.appendChild(this.panel);
        this.panel.style.minWidth = DomHandler.getWidth(this.button) + 'px';
        this.panel.style.zIndex = String(++DomHandler.zindex);
        // Set display directly so the panel is visible immediately, without depending
        // solely on Angular CD to update the [style.display] binding. Because pagebuilder is detached
        this.panel.style.display = 'block';
        this.panelVisible = true;
        // Awkward to account for change detection being on push in pagebuilder
        this.shown = true;
        this.cd.detectChanges();
        this.onShow();
        this.panelShown.emit();
    }

    hide(isMouseLeave: boolean = false,  force: boolean = false) {
        if (isMouseLeave && !this.hidePanelOnMouseLeave) {
            return;
        }
        if (!force && this.kebabMenus.some(subMenu => subMenu.panelVisible)) {
            return;
        }
        if (force) {
            this.kebabMenus.forEach(subMenu => subMenu.hide(false, true));
        }
        // Clear static reference when closing
        if (!this.isMenuItem && KebabMenuComponent.currentlyOpen === this) {
            KebabMenuComponent.currentlyOpen = null;
        }
        this.panelVisible = false;
        this.panel.remove();
        if (this.mouseMoveListener) {
            this.mouseMoveListener();
            this.mouseMoveListener = null;
        }
        this.unbindDocumentClickListener();
        this.removeEventListeners();
        if (isMouseLeave) {
            this.panelHidden.emit();
        }
    }

    alignPanel() {
        this.panel.style.marginTop = '0px';
        document.body.appendChild(this.panel);
        const referenceEl = this.parentButton || this.button;

        // Defaults to bottom left unless the viewport is too small to fit the panel
        DomHandler.absolutePosition(this.panel, referenceEl);
        this.panel.style.marginTop = '0px';
        const buttonRect = this.button.getBoundingClientRect();
        const panelRect = this.panel.getBoundingClientRect();
        if (this.isMenuItem) {
            this.panel.style.marginTop = '0px';
        }
        if (this.alignmentPriority === LayoutTypesEnum.vertical) {
            if (this.panelAlignCenter) {
                const refRect = referenceEl.getBoundingClientRect();
                this.panel.style.left = refRect.left + refRect.width / 2 - panelRect.width / 2 + 'px';
            } else if (this.panelPositionX === PanelPositionEnum.right) {
                this.panel.style.left = (buttonRect.left - (panelRect.width - buttonRect.width)) + 'px';
            }

            if (Math.round(panelRect.top) === Math.round(buttonRect.bottom)) { // Panel is positioned below the button
                this.panelPositionY = PanelPositionEnum.bottom;
                this.panel.style.marginTop = 3 + 'px';
            } else {
                this.panelPositionY = PanelPositionEnum.top;
                this.panel.style.marginTop = -3 + 'px';
            }
        } else {
            if (this.panelPositionX === PanelPositionEnum.left) {
                this.panel.style.left = buttonRect.left - panelRect.width - 15 + 'px';
            } else {
                this.panel.style.left = buttonRect.right + 15 + 'px';
            }
            const panelHeight = this.panel.offsetHeight;

            if (Math.round(panelRect.top) === Math.round(buttonRect.bottom)) { // Panel is positioned below the button
                this.panelPositionY = PanelPositionEnum.bottom;
                this.panel.style.top = buttonRect.top - 5 + 'px';
            } else {
                this.panelPositionY = PanelPositionEnum.top;
                this.panel.style.top = buttonRect.top - panelHeight - 5 + 'px';
            }
        }
    }

    closeOtherSubMenus(index: number): void {
        this.kebabMenus.forEach((menu, i) => {
            if (i !== index) {
                menu.hide();
            }
        });
    }

    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
                // Do not close the panel when clicking inside it, on the toggle button, or inside any
                // nested sub-panel (which is appended to document.body as a sibling, not a child of
                // this.panel). This guards against the document listener firing for panel-internal
                // clicks in cases where stopImmediatePropagation() is insufficient.
                if (
                    this.el.nativeElement.contains(event.target as Node)
                    || this.panel.contains(event.target as Node)
                    // || (event.target as Element).closest('.agpr-kebabMenu-panel')
                ) {
                    return;
                }
                this.hide();
                this.cd.detectChanges();
            });
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    ngOnDestroy() {
        this.hide();
        this.unbindDocumentClickListener();
        this.removeEventListeners();
    }

    addEventListeners() {
        if (this.isMenuItem && this.scrollableParentElements?.length) {
            this.scrollableParents = this.scrollableParentElements;
        } else {
            this.scrollableParents = DomHandler.getScrollableParents(this.el.nativeElement);
        }

        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.addEventListener('scroll', this.scrollListener);
            });
        }
        window.addEventListener('resize', this.scrollListener);
        window.addEventListener('scroll', this.scrollListener);
    }

    removeEventListeners() {
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', this.scrollListener);
            });
        }

        window.removeEventListener('resize', this.scrollListener);
        window.removeEventListener('scroll', this.scrollListener);
    }

    onPanelKeydown(event: KeyboardEvent) {
        // Tab on last item, or shift + tab on first item
        if (event.key === ActionKeys.TAB
            && (((event.target as HTMLElement).closest('li').classList.contains('agpr-kebabMenu-panel-item--last') && !event.shiftKey)
            || ((event.target as HTMLElement).closest('li').classList.contains('agpr-kebabMenu-panel-item--first') && event.shiftKey))) {
            event.preventDefault();
            // If kebab menu is "display: none", it cannot be focused manually.
            this.kebabMenuVisible = true;
            this.kebabMenu.addEventListener('focusout', this.onFocusOutFromKebabMenu);
            requestAnimationFrame(() => {
                (this.button.getElementsByTagName('BUTTON')[0] as HTMLElement).focus();
            });

            this.hide();
        }
    }

    onButtonKeydown(event: KeyboardEvent) {
        if (event.key === ActionKeys.TAB && this.panelVisible) {
            event.stopImmediatePropagation();
            if (event.shiftKey) {
                this.hide();
            } else {
                event.preventDefault();
                const elements = this.panel.getElementsByTagName('LI');
                let element = elements[0] as HTMLElement;
                if (element.getAttribute('tabindex') === '0') {
                    element.focus();
                } else {
                    element = element.querySelector('.agpr-button');
                    element.focus();
                }
            }
        }
    }

    private calculateNeedsScroll(): boolean {
        const itemHeight = 36;
        const maxHeight = 230;
        return (this.optionsToDisplay?.length ?? 0) * itemHeight > maxHeight;
    }

}
